import { DefaultApiGetFilterOptionsRequest, FilterOption } from "@coeff/api-admin-ext";
import { useQuery } from "@tanstack/react-query";
import React from "react";

import { QueryCacheKey } from "../../../api";
import { CheckboxListPopover } from "../../../components/CheckboxListPopover";
import {
  DataSourceTypeDisplayNames,
  getTimeZone,
  pluralizeString,
  useApiContext,
} from "../../../utils";

type Props = {
  table: DefaultApiGetFilterOptionsRequest["table"];
  domain: string;
  values: string[] | undefined;
  onChange: (values: string[]) => void;
  entityLabel?: string;
};

export const DataSourceFilterPopover: React.FC<Props> = ({
  values,
  onChange,
  domain,
  table,
  entityLabel = "Source",
}) => {
  const { adminExtApiClient } = useApiContext();

  const sourceFilterOptionsParams: DefaultApiGetFilterOptionsRequest = {
    table,
    filter_type: "data_source_type",
    domain,
    tz_name: getTimeZone(),
  };

  const { data: sourceFilterOptionsResponse } = useQuery(
    [QueryCacheKey.FILTER_OPTIONS, sourceFilterOptionsParams],
    () => adminExtApiClient.getFilterOptions(sourceFilterOptionsParams)
  );

  const options = (sourceFilterOptionsResponse?.data.options ?? []).map(option => ({
    ...option,
    label: DataSourceTypeDisplayNames[option.value] ?? option.label,
  }));

  return (
    <CheckboxListPopover
      hasSearch
      getLabel={() =>
        values === undefined || values.length === 0 || values.length === options.length
          ? `All ${entityLabel}s`
          : pluralizeString(entityLabel, values.length)
      }
      initialValues={values}
      options={options}
      onChange={values => onChange(values)}
      buttonProps={{
        className: "gray",
      }}
    />
  );
};

export const UserFilterPopover: React.FC<Props> = ({ values, domain, onChange, table }) => {
  const { adminExtApiClient } = useApiContext();

  const createdByFilterOptionsParams: DefaultApiGetFilterOptionsRequest = {
    table,
    filter_type: "created_by",
    domain,
    tz_name: getTimeZone(),
  };

  const { data: createdByFilterOptionsResponse } = useQuery(
    [QueryCacheKey.FILTER_OPTIONS, createdByFilterOptionsParams],
    () => adminExtApiClient.getFilterOptions(createdByFilterOptionsParams)
  );

  const options = createdByFilterOptionsResponse?.data.options ?? [];

  return (
    <CheckboxListPopover
      hasSearch
      getLabel={() =>
        values === undefined || values.length === 0 || values.length === options.length
          ? "All Users"
          : pluralizeString("User", values.length)
      }
      initialValues={values}
      options={options}
      onChange={values => onChange(values)}
      buttonProps={{
        className: "gray",
      }}
    />
  );
};

export const ScheduleFilterPopover: React.FC<Omit<Props, "options" | "table">> = ({
  values,
  onChange,
}) => {
  const options: FilterOption[] = [
    {
      label: "No Schedule",
      value: "no_schedule",
    },
    {
      label: "Hourly",
      value: "hourly",
    },
    {
      label: "Daily",
      value: "daily",
    },
    {
      label: "Weekly",
      value: "weekly",
    },
    {
      label: "Monthly",
      value: "monthly",
    },
  ];

  return (
    <CheckboxListPopover
      hasSearch
      getLabel={() =>
        values === undefined || values.length === 0 || values.length === options.length
          ? "All Schedules"
          : pluralizeString("Schedule", values.length)
      }
      initialValues={values}
      options={options}
      onChange={values => onChange(values)}
      buttonProps={{
        className: "gray",
      }}
    />
  );
};

export const RunStatusFilterPopover: React.FC<Omit<Props, "options" | "table" | "domain">> = ({
  values,
  onChange,
}) => {
  const options: FilterOption[] = [
    {
      label: "OK",
      value: "ok",
    },
    {
      label: "Run Error",
      value: "failed",
    },
  ];

  return (
    <CheckboxListPopover
      getLabel={() =>
        values === undefined || values.length === 0 || values.length === options.length
          ? "All Statuses"
          : values?.some(value => value === "failed")
          ? "Status Run Error"
          : "Status OK"
      }
      initialValues={values}
      options={options}
      onChange={values => onChange(values)}
      buttonProps={{
        className: "gray",
      }}
    />
  );
};

export const TriggerFilterPopover: React.FC<Omit<Props, "options" | "table" | "domain">> = ({
  values,
  onChange,
}) => {
  const options: FilterOption[] = [
    {
      label: "Manual",
      value: "manual",
    },
    {
      label: "Scheduled",
      value: "scheduled",
    },
  ];

  return (
    <CheckboxListPopover
      getLabel={() =>
        values === undefined || values.length === options.length
          ? "All Triggers"
          : values?.some(value => value === "manual")
          ? "Manual"
          : "Scheduled"
      }
      initialValues={values}
      options={options}
      onChange={values => onChange(values)}
      buttonProps={{
        className: "gray",
      }}
    />
  );
};
